import { Dialog, DialogContent, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

type IProps = {
  toggle: boolean;
  onClose: () => void;
  audioUrl: string;
  title: string;
};

const AudioModel: React.FC<IProps> = ({ toggle, onClose, audioUrl, title }) => {
  return (
    <Dialog
      open={toggle}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #e94560, #f5a623)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GraphicEqIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant='caption'
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1 }}
            >
              Now Playing
            </Typography>
            <Typography
              variant='subtitle1'
              sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size='small'
          sx={{
            color: 'rgba(255,255,255,0.6)',
            backgroundColor: 'rgba(255,255,255,0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white',
            },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pb: 3, pt: 2 }}>
        {/* Decorative waveform bars */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3px',
            height: 48,
            mb: 3,
          }}
        >
          {Array.from({ length: 40 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: `${20 + Math.sin(i * 0.8) * 14 + Math.cos(i * 0.4) * 10}px`,
                borderRadius: 2,
                background: 'linear-gradient(to top, #e94560, #f5a623)',
                opacity: 0.6 + (i % 3) * 0.1,
              }}
            />
          ))}
        </Box>

        {/* Audio player */}
        <Box
          sx={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 3,
            p: 2,
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <audio
            controls
            style={{ width: '100%', outline: 'none' }}
          >
            <source src={audioUrl} type='audio/wav' />
            Your browser does not support the audio tag.
          </audio>
        </Box>

        {/* Footer label */}
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.25)',
            mt: 2,
            letterSpacing: 0.5,
          }}
        >
          Use controls to play, pause and seek
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AudioModel;